import React from 'react'
import PropTypes from 'prop-types'
import {Text, Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {Flex, useMultiStyleConfig} from '@chakra-ui/react'
import InfoPopover from '../popovers/info-popover'
import {InfoIcon} from '../icons'
import {remCalc} from '../../theme/_utils'

/**
 * Omnibus pricing scenarios:
 * 1. Do not show the omnibus price when it is less than the current price or equal to the list price.
 * 2. Show the omnibus price when it is the same as the current price.
 * 3. Show the omnibus price and the discount otherwise.
 * the constants showOmnibusPrice and showOmnibusPriceDiscount are used to determine which scenario to show.
 */

const OmnibusPrice = ({
    priceData,
    currency,
    omnibusExpandedView,
    showDiscount = false,
    variant,
    showInfoPopover = false,
    isPlpCompareMobile = false,
}) => {
    const intl = useIntl()
    const {omnibusPrice, omnibusPriceDiscount, showOmnibusPrice, showOmnibusPriceDiscount} =
        priceData

    const styles = useMultiStyleConfig('OmnibusPrice', {variant})
    const priceTextProps = styles.price
    const isCompareVariant = variant === 'compare'
    const omnibusPriceDiscountText = `(-${intl.formatNumber(omnibusPriceDiscount, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })})`

    const omnibusPriceText = `${intl.formatNumber(omnibusPrice, {
        style: 'currency',
        currency,
    })}`
    const omnibusLabelText = intl.formatMessage({
        defaultMessage: 'Lowest Price last 30 days',
        id: 'last.lower.price.omnibus',
    })
    const omnibusLabelWithPriceText = `${omnibusLabelText} ${omnibusPriceText}`

    return (
        showOmnibusPrice &&
        (omnibusExpandedView ? (
            <Flex
                {...styles.container}
                flexDir={isPlpCompareMobile ? 'column' : styles?.container?.flexDir}
                alignItems={isPlpCompareMobile ? 'center' : styles?.container?.alignItems}
                textAlign={isPlpCompareMobile ? 'center' : styles?.container?.textAlign}
            >
                {isCompareVariant ? (
                    <Text {...styles.label}>
                        {omnibusLabelText}{' '}
                        <Text as="span" {...priceTextProps}>
                            {omnibusPriceText}
                        </Text>
                        {showOmnibusPriceDiscount && showDiscount && (
                            <Text as="span" {...styles.discount}>
                                {' '}
                                {omnibusPriceDiscountText}
                            </Text>
                        )}
                    </Text>
                ) : (
                    <>
                        <Text {...styles.label}>{omnibusLabelText}</Text>
                        <Text {...priceTextProps}>{omnibusPriceText}</Text>
                        {showOmnibusPriceDiscount && showDiscount && (
                            <Text {...styles.discount}>{omnibusPriceDiscountText}</Text>
                        )}
                    </>
                )}
                {showInfoPopover && (
                    <InfoPopover
                        trigger={
                            <Box display="inline-flex" alignItems="center" justifyContent="center">
                                <InfoIcon
                                    aria-label="Omnibus information"
                                    boxSize={remCalc(16)}
                                    color="foreground.neutral.default"
                                />
                            </Box>
                        }
                        variant="left-center"
                        title={null}
                        description={omnibusLabelWithPriceText}
                        popoverModifiers={[
                            {
                                name: 'preventOverflow',
                                options: {
                                    padding: 12,
                                    tether: true,
                                },
                            },
                            {
                                name: 'flip',
                                options: {
                                    fallbackPlacements: ['top', 'bottom', 'right', 'left'],
                                },
                            },
                        ]}
                    />
                )}
            </Flex>
        ) : (
            <Flex
                {...styles.container}
                flexDir={isPlpCompareMobile ? 'column' : styles?.container?.flexDir}
                alignItems={isPlpCompareMobile ? 'center' : styles?.container?.alignItems}
                textAlign={isPlpCompareMobile ? 'center' : styles?.container?.textAlign}
            >
                {isCompareVariant ? (
                    <Text {...styles.label}>
                        {omnibusLabelText}{' '}
                        <Text as="span" {...priceTextProps}>
                            {omnibusPriceText}
                        </Text>
                    </Text>
                ) : (
                    <>
                        <Text {...styles.label}>{omnibusLabelText}</Text>
                        <Text {...priceTextProps}>{omnibusPriceText}</Text>
                    </>
                )}
                {showInfoPopover && (
                    <InfoPopover
                        trigger={
                            <Box display="inline-flex" alignItems="center" justifyContent="center">
                                <InfoIcon
                                    aria-label="Omnibus information"
                                    boxSize={remCalc(16)}
                                    color="foreground.neutral.default"
                                />
                            </Box>
                        }
                        variant="left-center"
                        title={null}
                        description={omnibusLabelWithPriceText}
                        popoverModifiers={[
                            {
                                name: 'preventOverflow',
                                options: {
                                    padding: 12,
                                    tether: true,
                                },
                            },
                            {
                                name: 'flip',
                                options: {
                                    fallbackPlacements: ['top', 'bottom', 'right', 'left'],
                                },
                            },
                        ]}
                    />
                )}
            </Flex>
        ))
    )
}

OmnibusPrice.propTypes = {
    currency: PropTypes.string,
    priceData: PropTypes.shape({
        omnibusPrice: PropTypes.number,
        omnibusPriceDiscount: PropTypes.number,
        showOmnibusPrice: PropTypes.bool,
        showOmnibusPriceDiscount: PropTypes.bool,
    }),
    omnibusExpandedView: PropTypes.bool,
    showDiscount: PropTypes.bool,
    variant: PropTypes.string,
    showInfoPopover: PropTypes.bool,
    isPlpCompareMobile: PropTypes.bool,
}

export default OmnibusPrice
